import { Component, OnDestroy, OnInit } from '@angular/core';
import * as faceapi from '@vladmandic/face-api';
import { environment } from '../../environments/environment';
import { CommunicationService } from '../services/communication.service';
import { formatTimeUTC, generateTimeStamp, QueryablePromise } from '../utils';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { emotionFile, projectDataFile } from '../file-input/request-files-presets';
import { getDefaultFileName } from '../file-input/request-files';

// TODO consider using https://www.npmjs.com/package/@vladmandic/human, is more up to date
faceapi.env.monkeyPatch({
  Canvas: HTMLCanvasElement,
  Image: HTMLImageElement,
  ImageData,
  Video: HTMLVideoElement,
  createCanvasElement: () => document.createElement('canvas'),
  createImageElement: () => document.createElement('img'),
});

@Component({
  selector: 'app-webcam-emotion-recognition',
  templateUrl: './webcam-emotion-recognition.component.html',
  styleUrls: ['./webcam-emotion-recognition.component.scss'],
})
export class WebcamEmotionRecognitionComponent implements OnInit, OnDestroy {

  private recognitionInterval = 1000;

  private sad: number;
  private angry: number;
  private disgusted: number;
  private surprised: number;
  private happy: number;
  private neutral: number;
  private video: HTMLVideoElement;
  private canvas: HTMLCanvasElement;
  private displaySize: { width: number; height: number; };
  private emotionData = [];
  private timeStamp: string;
  private startEpoch = 0;
  private endEpoch = 0;
  private recordingMessage: boolean;
  private camReadyMessage: boolean;
  private wasRunning = false;
  private timer: NodeJS.Timeout;
  private shouldDelayLoad = false;
  private shouldDelayLoadTimer: NodeJS.Timeout;

  public modeLoadPromise: QueryablePromise<void>[];
  public cameraReady: boolean;


  private subscription = new Subscription();

  constructor(private comData: CommunicationService, private snackbar: MatSnackBar) {
  }

  async ngOnInit() {
    this.cameraReady = false;
    this.modeLoadPromise = this.loadModelsPromises();
    this.loadModels(this.modeLoadPromise);

    const recordingSub = this.comData.isRecording.subscribe((isRecording) => {
      this.recordingMessage = isRecording;
      if (this.recordingMessage === true) {
        this.startRecording();
      } else if (this.wasRunning && this.recordingMessage === false) {
        this.stopRecording();
      }
    });
    const emotionSub = this.comData.emotionRecognitionReady.subscribe(
      (camMessage) => (this.camReadyMessage = camMessage)
    );

    // document.addEventListener('keydown', (event) => {
    //   if (event.code === ScreenRecorderComponent.recordHotkey) {
    //     this.endEpoch = Date.now();
    //     this.timeStamp = generateTimeStamp(new Date());
    //     this.emotionData.push([this.timeStamp, 0, 0, 0, 0, 0, 0]);
    //   }
    // });

    this.subscription.add(recordingSub);
    this.subscription.add(emotionSub);
  }

  ngOnDestroy() {
    this.stopRecording();
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  startCamStream() {
    // null modelLoadPromise after a second to allow the progress bar to finish
    if (this.shouldDelayLoad) {
      setTimeout(() => {
        this.modeLoadPromise = null;
      }, 1000);
    }
    else {
      this.modeLoadPromise = null;
      clearTimeout(this.shouldDelayLoadTimer);
    }
    this.video = document.getElementById('video') as HTMLVideoElement;
    if (!this.video) throw new Error('Video element not found');
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => this.addCanPlayListener(stream))
        .catch((error) => {
          this.snackbar.open('Camera is not ready yet or camera permissions are missing', 'Dismiss');
          throw new Error('Error with camera stream: ' + error);
        });
    }
  }

  async addCanPlayListener(stream: MediaStream) {
    this.video.srcObject = stream;
    this.video.addEventListener('canplay', async () => this.onCanPlayVideo(), { passive: true });
  }

  async onCanPlayVideo() {
    this.displaySize = {
      width: this.video.width,
      height: this.video.height,
    };
    const canvas = faceapi.createCanvasFromMedia(this.video);
    this.video.parentNode.append(canvas);
    canvas.style.position = 'absolute';
    canvas.style.left = '0';
    canvas.style.top = '0';
    this.canvas = canvas;
    faceapi.matchDimensions(canvas, this.displaySize);
    // first face detection call takes 1400 ms, subsequent calls take about 50 ms
    this.timer = setInterval(async () => this.runEmotionRecognition(), this.recognitionInterval);
  }

  async runEmotionRecognition() {
    // const detections = await faceapi.detectSingleFace(this.video).withFaceLandmarks()
    // .withFaceExpressions().withFaceDescriptor();
    // const sfDetection = await faceapi.detectSingleFace(this.video, new faceapi.TinyFaceDetectorOptions());
    // console.log(sfDetection);
    console.time('getFaceEmotions');
    const detections = await this.getFaceEmotions();
    console.timeEnd('getFaceEmotions');
    // console.log(detections);
    this.timeStamp = generateTimeStamp(new Date());
    if (this.wasRunning) {
      if (detections === undefined) {
        this.emotionData.push([
          this.timeStamp,
          'NaN',
          'NaN',
          'NaN',
          'NaN',
          'NaN',
          'NaN',
        ]);
      } else {
        this.angry = detections.expressions.angry.valueOf();
        this.sad = detections.expressions.sad.valueOf();
        this.disgusted = detections.expressions.disgusted.valueOf();
        this.surprised = detections.expressions.surprised.valueOf();
        this.happy = detections.expressions.happy.valueOf();
        this.neutral = detections.expressions.neutral.valueOf();
        this.emotionData.push([
          this.timeStamp,
          this.angry,
          this.sad,
          this.disgusted,
          this.surprised,
          this.happy,
          this.neutral,
        ]);
      }
    }
    if (detections !== undefined) {
      const resizedDetections = faceapi.resizeResults(
        detections,
        this.displaySize
      );
      this.canvas
        .getContext('2d')
        .clearRect(0, 0, this.canvas.width, this.canvas.height);
      const minProbability = 0.05;
      /* if (this.wasRunning) {
      this.emotionData.push([this.timeStampString, this.angry, this.sad, this.disgusted, this.surprised,
      this.happy, this.neutral]);
    } */
      faceapi.draw.drawFaceExpressions(
        this.canvas,
        resizedDetections,
        minProbability
      );
      faceapi.draw.drawDetections(this.canvas, resizedDetections);
      if (!this.cameraReady) {
        this.setStatus(true);
        this.cameraReady = true;
      }
    }
  }

  async getFaceEmotions() {
    return faceapi
      .detectSingleFace(
        this.video,
        new faceapi.TinyFaceDetectorOptions()
      )
      .withFaceExpressions();
  }

  setStatus(camReadyBoolean: boolean) {
    this.comData.changeEmotionRecognitionReady(camReadyBoolean);
  }

  saveToCSV(emotionData) {
    const a = document.createElement('a');
    const headers = [
      'time',
      'angry',
      'sad',
      'disgusted',
      'surprised',
      'happy',
      'neutral',
    ];
    const csvData =
      headers + '\n' + emotionData.map((item) => item.join(',')).join('\n');
    const file = new Blob([csvData], { type: 'text/csv' });
    a.href = URL.createObjectURL(file);
    document.body.appendChild(a);
    a.download = getDefaultFileName(emotionFile);
    a.click();
    document.body.removeChild(a);
  }

  public modelLoadStatus() {
    // check all the promises in the array and how many are resolved
    if (!this.modeLoadPromise?.length) return 0;
    const resolved = this.modeLoadPromise.filter((p) => p.resolved || p.rejected);
    // return a value between 0 and 100
    const percentage = (resolved.length / this.modeLoadPromise.length) * 100;
    return percentage;
  }

  private loadModelsPromises(): QueryablePromise<void>[] {
    const modelUrl = environment.modelUrl;
    // if loading has taken more than a second, we let the progress bar finish
    this.shouldDelayLoadTimer = setTimeout(() => {
      this.shouldDelayLoad = true;
    }, 1000);
    // we use queryable promise to track the status of the promise
    return [
      new QueryablePromise(faceapi.nets.ssdMobilenetv1.loadFromUri(modelUrl)),
      new QueryablePromise(faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl)),
      new QueryablePromise(faceapi.nets.faceLandmark68Net.loadFromUri(modelUrl)),
      new QueryablePromise(faceapi.nets.faceExpressionNet.loadFromUri(modelUrl)),
      new QueryablePromise(faceapi.nets.faceRecognitionNet.loadFromUri(modelUrl)),
    ];
  }

  async loadModels(promises: QueryablePromise<void>[]) {
    const actualPromises = promises.map((p) => p.promise);
    return Promise.all(actualPromises).
      then(() => this.startCamStream());
  }

  startRecording() {
    this.wasRunning = true;
    this.startEpoch = Date.now();
    // initial junk log to syncronize for relative timestamp 0
    this.emotionData.push([
      generateTimeStamp(new Date()),
      'NaN',
      'NaN',
      'NaN',
      'NaN',
      'NaN',
      'NaN',
    ]);
  }

  stopRecording() {
    if (this.timer) {
      clearInterval(this.timer);
    }
    if (!this.wasRunning) return;

    this.endEpoch = Date.now();
    this.timeStamp = generateTimeStamp(new Date());
    this.emotionData.push([this.timeStamp, 0, 0, 0, 0, 0, 0]);
    this.wasRunning = false;
    const formattedEndTime = formatTimeUTC(this.endEpoch - this.startEpoch);

    this.saveToCSV(this.emotionData);
    this.emotionData = [];
    this.saveMorayCsv(formattedEndTime);
  }

  // TODO move this somewhere where it ALWAYS runs, as this is the project data used for syncing timestamps
  saveMorayCsv(endTime: string) {
    const headers = [
      'Elapsed Time',
      'Recording',
      'Task',
      'Event',
      'Details',
      'Application',
      'Owner',
      'Notes',
      'URL',
      'Page Title',
      'Title',
      'Score',
      'X [px]',
      'Y [px]',
      'Duration [ms]',
    ];
    const morayData: string[][] = [];
    morayData.push(headers);
    morayData.push(['0:00:00.00', '', '', 'Keystrokes', 'F9', '', '', '', '', '', '', '', '', '', '']);
    morayData.push(['0:00:00.00', '', '', 'Marker', 'K', '', '', '', '', '', '', '', '', '', '']);
    morayData.push(['0:00:00.00', '', '', 'Marker', 'A', '', '', '', '', '', '', '', '', '', '']);
    morayData.push([endTime, '', '', 'Marker', 'B', '', '', '', '', '', '', '', '', '', '']);
    morayData.push([endTime, '', '', 'Marker', 'L', '', '', '', '', '', '', '', '', '', '']);
    morayData.push([endTime, '', '', 'Keys`trokes', 'F9', '', '', '', '', '', '', '', '', '', '']);
    const csvDataWithBom = '\ufeff' + morayData.map((item) => item.join('\t')).join('\r\n');
    const blob = new Blob([csvDataWithBom], { type: 'text/csv' });

    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = getDefaultFileName(projectDataFile);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}
